/* AF — Product Recs (v4.2)
   Theme Editor safe hydration + in-card quick-add drawer
   ✅ Fix: Use Dawn sections pipeline after /cart/add.js so:
      - cart icon bubble updates instantly
      - cart drawer / notification opens automatically (no refresh)
*/

(function () {
  const SELECTORS = {
    host: 'af-product-recommendations',
    card: '.af-pr-card',
    open: '[data-af-qa-open]',
    close: '[data-af-qa-close]',
    add: '[data-af-qa-add]',
    drawer: '.af-pr-drawer',
    json: '[data-af-qa-json]',
    body: '[data-af-qa-body]',
  };

  const INIT_FLAG = 'data-af-pr-initialized';
  const LOADED_FLAG = 'data-af-pr-loaded';

  function qs(el, sel) {
    return el ? el.querySelector(sel) : null;
  }
  function qsa(el, sel) {
    return el ? Array.from(el.querySelectorAll(sel)) : [];
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  function closeCard(card) {
    if (!card) return;
    card.classList.remove('is-open');
    const drawer = qs(card, SELECTORS.drawer);
    if (drawer) drawer.hidden = true;
  }

  function closeAllExcept(exceptCard) {
    qsa(document, `${SELECTORS.card}.is-open`).forEach((c) => {
      if (c !== exceptCard) closeCard(c);
    });
  }

  function findVariant(variants, selections, optionNames) {
    return (
      variants.find((v) => {
        if (!v.available) return false;
        for (let i = 0; i < optionNames.length; i++) {
          const name = optionNames[i];
          const val = selections[name];
          if (!val) return false;
          const key = `option${i + 1}`;
          if (v[key] !== val) return false;
        }
        return true;
      }) || null
    );
  }

  // --- Dawn cart UI integration ---------------------------------------------

  function getSectionsToRequest() {
    const ids = [];
    // Bubble
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    // Drawer/Notification (Dawn)
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.querySelector('cart-notification')) ids.push('cart-notification');
    // Live region (Dawn)
    if (document.getElementById('cart-live-region-text')) ids.push('cart-live-region-text');
    return ids;
  }

  function openCartUI(state) {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(state);
      if (typeof cartDrawer.open === 'function') cartDrawer.open();
      return;
    }

    if (cartNotification && typeof cartNotification.renderContents === 'function') {
      cartNotification.renderContents(state);
      return;
    }

    // Fallback: apply any returned section HTML by matching IDs
    if (state?.sections) {
      Object.entries(state.sections).forEach(([id, html]) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = html;
      });
    }
  }

  async function addToCart(variantId, quantity = 1) {
    const sections = getSectionsToRequest();

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity }],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!res.ok) {
      let msg = 'Add to cart failed.';
      try {
        const err = await res.json();
        msg = err?.description || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }

    return res.json(); // includes { sections: { ... } } when requested
  }

  // --- Options UI ------------------------------------------------------------

  function buildOptionsUI(container, data, state) {
    container.innerHTML = '';
    if (data.hasOnlyDefaultVariant) return;

    const optionNames = data.options.map((o) => o.name);

    data.options.forEach((opt) => {
      const wrap = document.createElement('div');
      wrap.className = 'af-pr-opt';

      const label = document.createElement('div');
      label.className = 'af-pr-opt__name';
      label.textContent = `${opt.name}:`;
      wrap.appendChild(label);

      const row = document.createElement('div');
      row.className = 'af-pr-opt__row';

      opt.values.forEach((val) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'af-pr-opt__btn';
        btn.textContent = val;
        btn.setAttribute('aria-pressed', state.selections[opt.name] === val ? 'true' : 'false');

        btn.addEventListener('click', () => {
          state.selections[opt.name] = val;

          qsa(row, '.af-pr-opt__btn').forEach((b) => {
            b.setAttribute('aria-pressed', b.textContent === val ? 'true' : 'false');
          });

          state.currentVariant = findVariant(data.variants, state.selections, optionNames);

          // enabled only when valid variant exists
          state.addBtn.disabled = !state.currentVariant;

          // reset visual states when selection changes
          state.addBtn.classList.remove('is-adding', 'is-added');
          state.addBtn.textContent = state.addBtn.dataset.afLabel || state.addBtn.textContent;
        });

        row.appendChild(btn);
      });

      wrap.appendChild(row);
      container.appendChild(wrap);
    });
  }

  // --- Card wiring -----------------------------------------------------------

  function initCard(card) {
    if (!card || card.hasAttribute(INIT_FLAG)) return;
    card.setAttribute(INIT_FLAG, 'true');

    const drawer = qs(card, SELECTORS.drawer);
    const jsonEl = qs(card, SELECTORS.json);
    const bodyEl = qs(card, SELECTORS.body);
    const openBtn = qs(card, SELECTORS.open);
    const closeBtn = qs(card, SELECTORS.close);
    const addBtn = qs(card, SELECTORS.add);

    if (!drawer || !jsonEl || !bodyEl || !openBtn || !closeBtn || !addBtn) return;

    // store original label once
    if (!addBtn.dataset.afLabel) addBtn.dataset.afLabel = addBtn.textContent.trim() || 'ADD TO CART';

    const data = safeJsonParse(jsonEl.textContent);
    if (!data || !Array.isArray(data.variants)) return;

    const state = { selections: {}, currentVariant: null, addBtn };

    function setBtnState(mode) {
      addBtn.classList.remove('is-adding', 'is-added');
      if (mode === 'adding') addBtn.classList.add('is-adding');
      if (mode === 'added') addBtn.classList.add('is-added');
    }

    // default variant handling
    if (data.hasOnlyDefaultVariant) {
      const v =
        data.variants.find((vv) => vv.id === data.firstAvailableVariantId) ||
        data.variants.find((vv) => vv.available) ||
        data.variants[0] ||
        null;

      state.currentVariant = v;
      addBtn.disabled = !v;
    } else {
      addBtn.disabled = true;
    }

    function open() {
      closeAllExcept(card);

      state.selections = {};
      state.currentVariant = data.hasOnlyDefaultVariant ? state.currentVariant : null;

      addBtn.textContent = addBtn.dataset.afLabel;
      setBtnState('idle');

      buildOptionsUI(bodyEl, data, state);

      // button enabled if default variant only
      addBtn.disabled = data.hasOnlyDefaultVariant ? !state.currentVariant : true;

      card.classList.add('is-open');
      drawer.hidden = false;
    }

    function close() {
      closeCard(card);
    }

    openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      open();
    });

    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      close();
    });

    // click outside drawer closes (when open)
    card.addEventListener('click', (e) => {
      if (!card.classList.contains('is-open')) return;
      const withinDrawer = e.target.closest(SELECTORS.drawer);
      const withinPlus = e.target.closest(SELECTORS.open);
      if (!withinDrawer && !withinPlus) close();
    });

    addBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // if disabled, do nothing
      if (addBtn.disabled) return;

      const variantId = data.hasOnlyDefaultVariant ? data.firstAvailableVariantId : state.currentVariant?.id;
      if (!variantId) return;

      addBtn.disabled = true;
      setBtnState('adding');
      addBtn.textContent = 'ADDING…';

      try {
        const cartState = await addToCart(variantId, 1);

        // ✅ critical fix: update bubble + open drawer/notification immediately
        openCartUI(cartState);

        setBtnState('added');
        addBtn.textContent = 'ADDED ✓';

        // optional close after success
        setTimeout(() => {
          close();
        }, 450);

        // restore label for next open
        setTimeout(() => {
          addBtn.textContent = addBtn.dataset.afLabel;
          setBtnState('idle');
          addBtn.disabled = data.hasOnlyDefaultVariant ? false : true;
        }, 1000);
      } catch (err) {
        console.warn(err);
        setBtnState('idle');
        addBtn.textContent = 'ERROR';
        setTimeout(() => {
          addBtn.textContent = addBtn.dataset.afLabel;
          addBtn.disabled = false; // let them retry
        }, 1200);
      }
    });
  }

  function initQuickAdd(root) {
    qsa(root || document, SELECTORS.card).forEach(initCard);
  }

  // --- Host hydration --------------------------------------------------------

  async function hydrateHost(host) {
    if (!host || host.hasAttribute(LOADED_FLAG)) return;
    const url = host.dataset.url;
    if (!url) return;

    host.setAttribute(LOADED_FLAG, 'true');

    try {
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (!res.ok) throw new Error(`Recommendations fetch failed: ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(SELECTORS.host);

      if (!fresh) return;

      host.innerHTML = fresh.innerHTML;
      initQuickAdd(host);
    } catch (e) {
      console.warn('[AF Product Recs] hydrate failed', e);
      host.removeAttribute(LOADED_FLAG);
    }
  }

  function observeHosts() {
    const hosts = qsa(document, SELECTORS.host);
    if (!hosts.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            hydrateHost(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '250px 0px' }
    );

    hosts.forEach((h) => io.observe(h));
  }

  // --- Boot -----------------------------------------------------------------

  document.addEventListener('DOMContentLoaded', () => {
    initQuickAdd(document);
    observeHosts();

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      qsa(document, `${SELECTORS.card}.is-open`).forEach(closeCard);
    });

    // Theme editor / dynamic DOM changes: hydrate any un-loaded host nodes that appear
    const mo = new MutationObserver(() => {
      qsa(document, SELECTORS.host).forEach((h) => {
        if (!h.hasAttribute(LOADED_FLAG)) hydrateHost(h);
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  });

  // Theme Editor safe re-init on section load
  document.addEventListener('shopify:section:load', (e) => {
    initQuickAdd(e.target);
    qsa(e.target, SELECTORS.host).forEach((h) => {
      if (!h.hasAttribute(LOADED_FLAG)) hydrateHost(h);
    });
  });
})();
